import {Button} from 'antd'
import classNames from 'classnames'
import {Formik} from 'formik'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {NullAsDefault, renderIf} from 'views/Shared'
import {renderIfElse} from "../../views/Shared"

const StepDots = ({current, total}) => (
    <div className="flex items-center justify-center py-5 px-8">
        {Array.from({length: total}).map((_, i) => (
            <React.Fragment key={i}>
                <div
                    className={classNames(
                        'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 flex-shrink-0 select-none',
                        i < current
                            ? 'bg-green-500 text-white shadow-sm'
                            : i === current
                                ? 'bg-primary text-white shadow-md ring-4 ring-primary ring-opacity-20'
                                : 'bg-color-100 text-color-400'
                    )}>
                    {i < current ? '✓' : i + 1}
                </div>
                {i < total - 1 && (
                    <div
                        className={classNames(
                            'h-0.5 flex-1 mx-2 max-w-20 transition-colors duration-500',
                            i < current ? 'bg-green-400' : 'bg-color-100'
                        )}
                    />
                )}
            </React.Fragment>
        ))}
    </div>
)

export const StepsForm = ({
                              className = 'px-4 pb-2',
                              children,
                              routes = [],
                              initialValues = {},
                              renderBottom,
                              onSubmit,
                              onCancel,
                              isLoading,
                              faded = true,
                              actionBarCenter = false,
                              stepHeader = false,
                              validate = NullAsDefault,
                              ...propsRest
                          }) => {
    const t = useTranslate()
    const [currentIndex, setCurrentIndex] = useState(0)
    const handleOnBack = () => setCurrentIndex(Math.max(0, currentIndex - 1))
    const C = routes[currentIndex].component
    const stepValidate = routes[currentIndex]?.validate || (() => ({}))

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={stepValidate}
            {...propsRest}>
            {({handleChange, handleSubmit, values, setTouched, setErrors, validateForm, ...rest}) => {
                const handleOnOk = async () => {
                    const stepFields = routes[currentIndex]?.fields || []
                    const allErrors = await validateForm(values)
                    const stepErrorKeys = Object.keys(allErrors).filter(k => stepFields.includes(k))

                    if (stepErrorKeys.length > 0) {
                        const touchedPatch = stepFields.reduce((acc, f) => {
                            acc[f] = true
                            return acc
                        }, {})
                        setTouched(prev => ({...(prev || {}), ...touchedPatch}))
                        return
                    }

                    if (currentIndex === routes.length - 1) {
                        handleSubmit()
                    } else {
                        setCurrentIndex(i => Math.min(routes.length - 1, i + 1))
                    }
                }

                return (
                    <div className="flex flex-col">
                        {children && children(currentIndex)}

                        {/* Step header — fixed */}
                        <div className="flex-shrink-0">
                            {renderIf(
                                stepHeader,
                                <StepDots current={currentIndex} total={routes.length}/>
                            )}
                            {renderIf(
                                !!routes[currentIndex]?.title,
                                <div className="text-center font-bold text-2xl px-4 uppercase leading-snug tracking-wider">
                                    {t(routes[currentIndex].title)}
                                </div>
                            )}
                            {renderIf(
                                !!routes[currentIndex]?.description,
                                <div className="text-center text-xs font-semibold text-color-300 tracking-wide uppercase mt-1 mb-2 px-4">
                                    {t(routes[currentIndex].description)}
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div
                            key={currentIndex}
                            className={classNames(
                                'flex flex-col',
                                className,
                                faded && 'animated fadeIn faster'
                            )}>
                            <C
                                {...{
                                    currentIndex,
                                    values,
                                    onChange: handleChange,
                                    onNext: handleOnOk,
                                    onBack: handleOnBack,
                                    onCancel,
                                    ...propsRest,
                                    ...rest,
                                }}
                            />
                        </div>

                        {/* Footer — fixed */}
                        {renderBottom ? (
                            renderBottom({
                                currentIndex,
                                values,
                                onChange: handleChange,
                                onNext: handleOnOk,
                                onBack: handleOnBack,
                                ...rest,
                            })
                        ) : (
                            <div className="mt-3 pt-3 pb-4 border-t border-color-50 flex flex-col flex-shrink-0 gap-2">
                                <Button
                                    color="primary" variant="solid"
                                    loading={isLoading}
                                    onClick={handleOnOk}
                                    className="w-full button-rounded-lg no-border font-bold"
                                    style={{height: 48, fontSize: 15}}
                                    disabled={!rest.isValid || !rest.dirty}>
                                    {currentIndex === routes.length - 1 ? t('submit') : t('next')}
                                </Button>
                                {renderIfElse(
                                    routes.length > 1,
                                    <button
                                        type="button"
                                        disabled={currentIndex === 0}
                                        onClick={handleOnBack}
                                        className="w-full py-2 text-sm text-color-300 hover:text-color-100 transition-colors disabled:opacity-40">
                                        {t('back')}
                                    </button>,
                                    renderIf(
                                        onCancel,
                                        <button
                                            type="button"
                                            onClick={onCancel}
                                            className="w-full py-2 text-sm text-color-300 hover:text-color-100 transition-colors">
                                            {t('close')}
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                )
            }}
        </Formik>
    )
}
